import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  getHostConciergeOutreachPreview,
  sendHostConciergeOutreachEmail,
} from '../../store/thunks/HostActions';

function formatMoney(value) {
  const number = Number(value || 0);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Math.round(number));
}

function opportunityRows(report) {
  return [
    ['1-night gap nights', report?.oneNightGap],
    ['2-night gap nights', report?.twoNightGap],
    ['Early check-in', report?.earlyCheckIn],
  ].map(([label, metric = {}]) => ({
    label,
    count: Number(metric?.count || 0),
    low: Number(metric?.minPotentialRevenue || 0),
    high: Number(metric?.maxPotentialRevenue || 0),
  }));
}

function payloadError(payload, fallback) {
  return payload?.error || payload?.message || fallback;
}

export default function ConciergeOutreachReport({
  host,
  setSuccessMessage,
  setErrorMessage,
}) {
  const dispatch = useDispatch();
  const hid = host?._id;
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({
    from: '',
    to: '',
    subject: '',
    bodyText: '',
  });

  const rows = useMemo(
    () => opportunityRows(preview?.report),
    [preview]
  );
  const highTotal = rows.reduce((sum, row) => sum + row.high, 0);
  const hasData = Boolean(preview?.hasReportData);
  const alreadySubscribed = Boolean(preview?.alreadySubscribed);
  const canSend = hasData && !alreadySubscribed && !sending && form.to && form.subject && form.bodyText;

  const loadPreview = useCallback(async () => {
    if (!hid) return;
    setLoading(true);
    setErrorMessage?.(null);
    try {
      const action = await dispatch(getHostConciergeOutreachPreview(hid));
      if (getHostConciergeOutreachPreview.fulfilled.match(action)) {
        const nextPreview = action.payload?.preview || null;
        setPreview(nextPreview);
        setForm({
          from: '',
          to: nextPreview?.defaults?.to || '',
          subject: nextPreview?.defaults?.subject || '',
          bodyText: nextPreview?.defaults?.bodyText || '',
        });
      } else {
        setErrorMessage?.(
          payloadError(action.payload, 'Failed to load concierge outreach preview')
        );
      }
    } catch (error) {
      setErrorMessage?.('Failed to load concierge outreach preview');
    } finally {
      setLoading(false);
    }
  }, [dispatch, hid, setErrorMessage]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  const handleFieldChange = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleSend = async () => {
    if (!canSend || !hid) return;
    setSending(true);
    setErrorMessage?.(null);
    setSuccessMessage?.(null);
    try {
      const action = await dispatch(
        sendHostConciergeOutreachEmail({
          hid,
          from: form.from,
          to: form.to,
          subject: form.subject,
          bodyText: form.bodyText,
        })
      );
      if (sendHostConciergeOutreachEmail.fulfilled.match(action)) {
        setSuccessMessage?.('Concierge subscription report email sent.');
      } else {
        setErrorMessage?.(
          payloadError(action.payload, 'Failed to send concierge outreach email')
        );
      }
    } catch (error) {
      setErrorMessage?.('Failed to send concierge outreach email');
    } finally {
      setSending(false);
    }
  };

  if (!hid) return null;

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-5 border border-[#F1F1F1]">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#EF4949]">
            Concierge subscription outreach
          </p>
          <h2 className="text-[20px] font-bold text-gray-900 mt-1">
            Revenue opportunity report
          </h2>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Send an editable subscription email using cached 2026 gap-night and
            early-check-in signals. CTA points hosts to subscribe at $8 per
            active property each month.
          </p>
        </div>
        <button
          type="button"
          onClick={loadPreview}
          disabled={loading}
          className="px-3 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {loading ? 'Loading...' : 'Refresh preview'}
        </button>
      </div>

      {alreadySubscribed && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          This host already has an active Concierge subscription, so the send
          action is disabled.
        </div>
      )}

      {!loading && !hasData && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          No cached 2026 gap-night or early-check-in opportunity signals are
          available for this host yet.
        </div>
      )}

      {hasData && (
        <div className="mb-5 overflow-hidden rounded-xl border border-gray-200">
          <div className="flex flex-wrap items-center justify-between gap-2 bg-gray-50 px-4 py-3">
            <p className="text-xs font-bold uppercase tracking-[0.12em] text-gray-500">
              {preview?.report?.period || '2026 YTD'} signals
            </p>
            <p className="text-sm font-bold text-gray-900">
              Up to {formatMoney(highTotal)} potential
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-[0.08em] text-gray-500">
                  <th className="px-4 py-3 font-bold">Signal</th>
                  <th className="px-4 py-3 font-bold text-right">Count</th>
                  <th className="px-4 py-3 font-bold text-right">Low</th>
                  <th className="px-4 py-3 font-bold text-right">High</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.label} className="border-b border-gray-100 last:border-0">
                    <td className="px-4 py-3 font-semibold text-gray-900">{row.label}</td>
                    <td className="px-4 py-3 text-right text-gray-700">{row.count}</td>
                    <td className="px-4 py-3 text-right text-gray-700">{formatMoney(row.low)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">{formatMoney(row.high)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-3">
          <label className="block">
            <span className="block text-sm font-semibold text-gray-700 mb-1">To</span>
            <input
              type="text"
              value={form.to}
              onChange={(event) => handleFieldChange('to', event.target.value)}
              placeholder="host@example.com"
              className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-[#EF4949] focus:border-[#EF4949]"
            />
          </label>
          <label className="block">
            <span className="block text-sm font-semibold text-gray-700 mb-1">From alias</span>
            <input
              type="text"
              value={form.from}
              onChange={(event) => handleFieldChange('from', event.target.value)}
              placeholder="Optional Gmail alias"
              className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-[#EF4949] focus:border-[#EF4949]"
            />
          </label>
          <label className="block">
            <span className="block text-sm font-semibold text-gray-700 mb-1">Subject</span>
            <input
              type="text"
              value={form.subject}
              onChange={(event) => handleFieldChange('subject', event.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-[#EF4949] focus:border-[#EF4949]"
            />
          </label>
        </div>

        <div>
          <label className="block">
            <span className="block text-sm font-semibold text-gray-700 mb-1">Editable email body</span>
            <textarea
              value={form.bodyText}
              onChange={(event) => handleFieldChange('bodyText', event.target.value)}
              rows={12}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm leading-6 focus:ring-2 focus:ring-[#EF4949] focus:border-[#EF4949]"
            />
          </label>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg bg-gray-50 border border-gray-100 px-4 py-3">
        <p className="text-xs text-gray-500">
          The email uses aggregate analytics only. No guest names, raw comments,
          reservation blobs, or GA event names are included.
        </p>
        <button
          type="button"
          onClick={handleSend}
          disabled={!canSend}
          className="px-4 py-2 rounded-lg bg-[#EF4949] text-white text-sm font-bold hover:bg-[#D93F3F] disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {sending ? 'Sending...' : 'Send subscription email'}
        </button>
      </div>
    </div>
  );
}
